import type {
  FormatOptions,
  FormatResult,
  LintOptions,
  LintReport,
  RepairOptions,
  RepairResult,
} from '@jsonfmt/core';

/**
 * The wire format between `services/analyzer.ts` and `services/worker/analyze.worker.ts`.
 *
 * STRUCTURED-CLONE-SAFE IS A HARD CONSTRAINT, not a style note. Everything below is plain
 * data plus `Set` (which `structuredClone` supports and which `RepairOptions.disabled`
 * uses): no functions, no class instances, no `undefined`-only sentinels that lose meaning,
 * no cycles. That is also why `Diagnostic` carries `edits: TextEdit[]` rather than an
 * `apply` closure (ADR-0008).
 *
 * WHAT MUST NEVER APPEAR HERE: `tokens` or a CST. Cloning hundreds of thousands of token
 * objects costs more than the analysis itself, so a Worker added to unload the main thread
 * would start loading it. The response carries `LintReport`, which is cheap (ADR-0010). A
 * future tab that needs structure above the soft limit adds a `project` REQUEST variant —
 * the worker already keeps the last `AnalyzedDocument` keyed by `version` — and a
 * projection, not a new architecture (docs/ARCHITECTURE.md §8.4).
 */

/**
 * Monotonic per-request counter. The analyzer keeps one in-flight request and one pending
 * slot; a response whose `seq` is older than the newest request is DISCARDED (ADR-0009).
 */
export type Seq = number;

/** `DocumentSnapshot.version` of the text being analysed. Echoed back on every response. */
export type DocumentVersion = number;

interface RequestBase {
  readonly seq: Seq;
  readonly version: DocumentVersion;
  readonly source: string;
}

export type WorkerRequest =
  | (RequestBase & { readonly kind: 'lint'; readonly options?: Partial<LintOptions> })
  | (RequestBase & { readonly kind: 'format'; readonly options?: Partial<FormatOptions> })
  | (RequestBase & { readonly kind: 'repair'; readonly options?: Partial<RepairOptions> });

interface ResponseBase {
  readonly seq: Seq;
  readonly version: DocumentVersion;
}

export type WorkerResponse =
  | (ResponseBase & { readonly kind: 'lint'; readonly report: LintReport })
  | (ResponseBase & { readonly kind: 'format'; readonly result: FormatResult })
  | (ResponseBase & { readonly kind: 'repair'; readonly result: RepairResult })
  /**
   * An Error instance does not survive `structuredClone` usefully, so a throw inside the
   * worker comes back as data. The analyzer rejects the matching promise with it; nothing
   * from the user's document is ever put in `message`.
   */
  | (ResponseBase & {
      readonly kind: 'error';
      readonly name: string;
      readonly message: string;
    });

/** Narrowing helper for the worker's `onmessage`, which is typed as `unknown`. */
export function isWorkerRequest(value: unknown): value is WorkerRequest {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as { kind?: unknown; seq?: unknown; source?: unknown };
  return (
    (candidate.kind === 'lint' || candidate.kind === 'format' || candidate.kind === 'repair') &&
    typeof candidate.seq === 'number' &&
    typeof candidate.source === 'string'
  );
}
